// Resolving a statement's payment plan into concrete payments.
//
// A StatementPayment carries real `statement_date` / `due_date` values, so every
// plan resolves exactly: "spread over 3 business days" knows which weekend and
// which Ontario bank holidays it is skipping, and there is no day-of-month to
// clamp in February. The resolved list
// is what the modal previews and what gets stored as `splits`; `payment_plan` +
// `plan_count` are stored alongside so the rule that produced it is not lost and
// can be re-derived on edit.
//
// Dates are handled as ISO "YYYY-MM-DD" strings at the boundaries (that is what
// <input type="date"> reads and writes, and what the API stores) and as local
// Date objects in between. Parsing goes through `parseISO` rather than
// `new Date(str)`, which would read a bare "2026-07-24" as UTC midnight and shift
// it a day backwards for anyone west of Greenwich.

// The Ontario banking calendar lives in @utils because the Schedule-a-payment
// weekday split needs it too — see ontarioBankingDays.js for why it layers two
// bank closures on top of the shared employment-law holiday map.
import { isBankingDay, nonBankingReason, prevBankingDay } from "@utils/ontarioBankingDays";

export { nonBankingReason, isBankingDay, prevBankingDay };

export const PAYMENT_PLANS = [
  { value: "business_days", label: "Spread over N business days" },
  { value: "weekly_installments", label: "N weekly installments" },
  { value: "single_payment", label: "Single payment on a date" },
  { value: "full_on_due", label: "Full balance on the due date" },
  { value: "custom_split", label: "Custom split" },
];

// Plans that need a count, and what to call it in the UI.
export const COUNT_LABELS = {
  business_days: "Number of business days",
  weekly_installments: "Number of weekly installments",
};

/** "2026-07-24" -> Date at LOCAL midnight (never UTC — see the note above). */
export function parseISO(s) {
  if (!s) return null;
  const [y, m, d] = String(s).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/** Date -> "YYYY-MM-DD", read in local time to match `parseISO`. */
export function toISO(d) {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

/**
 * Whole days from `a` to `b`, both ISO "YYYY-MM-DD"; negative when `b` precedes
 * `a`, null when either is unparseable. Rounded, because two local midnights are
 * 23 or 25 hours apart across a DST boundary.
 */
export function daysBetween(aISO, bISO) {
  const a = parseISO(aISO);
  const b = parseISO(bISO);
  if (!a || !b) return null;
  return Math.round((b - a) / 86400000);
}

/**
 * Payments falling on a day the bank does not move money, each with its reason.
 *
 * The generated plans already avoid these days, so in practice this catches the
 * hand-authored ones: a custom row or a single payment date typed onto a holiday.
 */
export function planNonBankingDays(payments = []) {
  return payments
    .map((p) => ({ payment: p, reason: nonBankingReason(p.date) }))
    .filter((x) => x.reason);
}

/**
 * Split `total` into `n` parts that sum EXACTLY to it. Works in cents and hands
 * the leftover pennies to the earliest payments, so 1000/3 becomes
 * 333.34 + 333.33 + 333.33 rather than three parts that quietly lose a cent.
 */
export function splitEvenly(total, n) {
  const cents = Math.round(Number(total) * 100);
  if (!Number.isFinite(cents) || n < 1) return [];
  const base = Math.floor(cents / n);
  let remainder = cents - base * n;
  return Array.from({ length: n }, () => {
    const extra = remainder > 0 ? 1 : 0;
    remainder -= extra;
    return (base + extra) / 100;
  });
}

/**
 * Resolve a plan into `[{ date: Date, iso: string, amount: number }]`.
 *
 * Returns [] when the inputs cannot produce a payment (no balance, no due date,
 * missing count) rather than throwing — the modal renders the preview on every
 * keystroke, including half-filled states.
 */
export function resolvePlan({ plan, balance, dueDate, count, singleDate, customSplits = [] }) {
  const amount = Number(balance);
  const at = (d, amt) => ({ date: d, iso: toISO(d), amount: amt });

  if (plan === "custom_split") {
    // Custom rows are authored directly — amounts are whatever was typed, which
    // is why the modal shows the running total against the balance and blocks
    // saving until they agree.
    return customSplits
      .filter((s) => s.date && s.amount !== "" && s.amount != null)
      .map((s) => at(parseISO(s.date), Number(s.amount)))
      .filter((p) => p.date)
      .sort((a, b) => (a.iso < b.iso ? -1 : 1));
  }

  if (!amount || amount <= 0) return [];

  if (plan === "single_payment") {
    // A TYPED date is honoured exactly as typed, closure or not, so the modal can
    // flag it instead of quietly moving a date someone chose on purpose. Only the
    // fallback — the due date, which the user has not touched — is pulled back to
    // a banking day, so merely picking this plan cannot raise a warning about a
    // date nobody entered.
    if (singleDate) {
      const typed = parseISO(singleDate);
      return typed ? [at(typed, amount)] : [];
    }
    const due = parseISO(dueDate);
    return due ? [at(prevBankingDay(due), amount)] : [];
  }

  const due = parseISO(dueDate);
  if (!due) return [];

  // Pulled back to a banking day: a due date on a Sunday or on Canada Day is not
  // a date the payment can clear on. Backward, never forward — the bank being
  // closed is no reason to pay the card late.
  if (plan === "full_on_due") return [at(prevBankingDay(due), amount)];

  const n = Number(count);
  if (!n || n < 1) return [];
  const amounts = splitEvenly(amount, n);

  // Both counted plans are anchored to the due date and counted BACKWARD, so the
  // final payment lands on it and the earlier ones fall inside the statement
  // window. Counting forward instead put every payment after N > 1 past the due
  // date, i.e. paid the card late by construction.
  if (plan === "weekly_installments") {
    return amounts.map((amt, i) => at(prevBankingDay(addDays(due, (i - (n - 1)) * 7)), amt));
  }

  if (plan === "business_days") {
    // Weekends and Ontario bank holidays are both skipped, so "3 business days"
    // means three days money can actually move. When the due date is itself a
    // closure the last payment is the banking day before it, not after.
    const out = [];
    let cursor = new Date(due);
    while (out.length < n) {
      if (isBankingDay(cursor)) out.unshift(new Date(cursor));
      cursor = addDays(cursor, -1);
    }
    return out.map((d, i) => at(d, amounts[i]));
  }

  return [];
}

/**
 * The payments that fall outside the statement → due window, or null when they
 * all fit.
 *
 * A plan pays ONE statement, so its payments belong on or between the two dates
 * that bound it: a payment before the statement date pays a balance that did not
 * exist yet, and one after the due date pays the card late. `gapDays` is the
 * width of the window, which is what a too-large `plan_count` overruns.
 */
export function planWindowIssue({ payments = [], statementDate, dueDate }) {
  if (!payments.length || !statementDate || !dueDate) return null;
  const before = payments.filter((p) => p.iso && p.iso < statementDate);
  const after = payments.filter((p) => p.iso && p.iso > dueDate);
  if (!before.length && !after.length) return null;
  return { before, after, gapDays: daysBetween(statementDate, dueDate) };
}

/** Sum of a resolved plan, rounded to cents. */
export function planTotal(payments) {
  return Math.round(payments.reduce((s, p) => s + (Number(p.amount) || 0), 0) * 100) / 100;
}

/** Shape the API stores: ISO date + a decimal string amount. */
export function toSplitsPayload(payments) {
  return payments.map((p) => ({ date: p.iso, amount: Number(p.amount).toFixed(2) }));
}

// ─── saved plans (StatementPlan) ─────────────────────────────────────────────
//
// A saved plan is this modal's shape minus the two things that change every
// month: the balance and the calendar dates. It stores a statement/due DAY of the
// month instead, so one plan serves every month and the user supplies only the
// amount.

/** Days in a 1-indexed month. */
function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function clampedISO(year, month, day) {
  return toISO(new Date(year, month - 1, Math.min(day, daysInMonth(year, month))));
}

function nextMonth(year, month) {
  return month === 12 ? [year + 1, 1] : [year, month + 1];
}

/**
 * The real `{ statementDate, dueDate }` a plan produces for `monthISO` ("2026-09").
 *
 * MIRRORS `StatementPlan.derive_dates` in the backend, and the server exposes it at
 * `GET /api/statement-plan/{id}/dates/?month=` as the authoritative definition.
 * Duplicated here on purpose: it is a clamp and an offset, no calendar knowledge,
 * and pre-filling a modal on every card selection should not cost a round trip.
 *
 * The due month comes from `due_month_offset` and is NEVER inferred. It used to be
 * guessed by comparing the two day numbers, which cannot work: "the 3rd → the
 * 24th" is a valid 21-day cycle and a valid 52-day one, and the day pair is
 * identical either way.
 *
 * One rule survives: if clamping leaves the due date on or before the statement
 * date, it rolls one more month. Reachable for real — statement day 28, due day
 * 31, offset 0, February.
 */
export function deriveStatementDates(plan, monthISO) {
  const [year, month] = String(monthISO || "")
    .split("-")
    .map(Number);
  if (!year || !month || !plan) return { statementDate: "", dueDate: "" };

  const statementDay = Number(plan.statement_day);
  const dueDay = Number(plan.due_day);
  const statementDate = clampedISO(year, month, statementDay);

  // `?? 1` not `|| 1`: an explicit 0 means "same month" and must survive.
  const offset = Number(plan.due_month_offset ?? 1);
  let [dueYear, dueMonth] = [year, month];
  for (let i = 0; i < offset; i += 1) [dueYear, dueMonth] = nextMonth(dueYear, dueMonth);
  let dueDate = clampedISO(dueYear, dueMonth, dueDay);
  if (dueDate <= statementDate) {
    [dueYear, dueMonth] = nextMonth(dueYear, dueMonth);
    dueDate = clampedISO(dueYear, dueMonth, dueDay);
  }
  return { statementDate, dueDate };
}

/**
 * A saved plan as modal form values for `monthISO`. `balance` is left empty — it
 * is the one thing the plan cannot know and the only thing left to type.
 *
 * Custom-split rows come back with their dates resolved but NO amounts: those
 * depend on the balance, so the modal fills them in once it is entered.
 */
export function planToFormValues(plan, monthISO) {
  const { statementDate, dueDate } = deriveStatementDates(plan, monthISO);
  return {
    from_bank_account: plan.from_bank_account ?? "",
    to_card_account: plan.to_card_account ?? "",
    statement_date: statementDate,
    due_date: dueDate,
    balance: "",
    payment_plan: plan.payment_plan ?? "full_on_due",
    plan_count: plan.plan_count ?? "",
    single_date: "",
    // A plan is never a custom split, so there is never a row to rebuild.
    custom: [{ date: "", amount: "" }],
    notes: plan.notes ?? "",
  };
}

/**
 * The modal's current form as a StatementPlan payload.
 *
 * The dates collapse to their day-of-month — that is the whole trick, and it is
 * why a plan survives into next month. Nothing else here needs converting: every
 * plannable payment_plan rebuilds its own payments from the balance and the due
 * date, so the plan carries the rule and not a list of payments.
 */
export function formValuesToPlan(form) {
  const statementDate = parseISO(form.statement_date);
  const dueDate = parseISO(form.due_date);
  const needsCount = COUNT_LABELS[form.payment_plan] != null;

  return {
    from_bank_account: form.from_bank_account,
    to_card_account: form.to_card_account,
    statement_day: statementDate ? statementDate.getDate() : null,
    due_day: dueDate ? dueDate.getDate() : null,
    // COMPUTED, never asked for. Both real dates are on screen at this moment,
    // so the month gap is a fact rather than something to infer from day numbers.
    due_month_offset:
      statementDate && dueDate
        ? dueDate.getFullYear() * 12 +
          dueDate.getMonth() -
          (statementDate.getFullYear() * 12 + statementDate.getMonth())
        : 1,
    payment_plan: form.payment_plan,
    plan_count: needsCount ? Number(form.plan_count) : null,
    notes: form.notes ?? "",
  };
}
